/**
 * A tiny, dependency-free syntax highlighter for Askdown source.
 *
 * It is shared by the server (landing page showcase, spec code blocks) and
 * the client (the builder's editor overlay, which bundles this file with
 * esbuild). It tokenizes line by line with a small state machine that mirrors
 * the parser's block structure: front matter, questions (until a blank line,
 * page break or next question), fenced code and Markdown content.
 *
 * Output is HTML using `<span class="hl-…">` wrappers. Every piece of source
 * text is escaped.
 */
import { escapeHtml, type Diagnostic } from '@askdown/core';

export interface LineMark {
  severity: 'error' | 'warning';
  /** 1-based column */
  column: number;
  /** 1-based, exclusive; undefined marks to the end of the line. */
  endColumn?: number;
}

const QUESTION = /^(\?\s+)(.*)$/;
const PAGE_BREAK = /^(---+)(\s*)(?:(\{[^}]*\})(\s*))?(?:(->)(\s*)(\S+))?\s*$/;
const PAGE_JUMP = /^(->)(\s*)(\S+)\s*$/;
const TYPE_SPEC = /^(\s*)(\[)([A-Za-z][\w-]*)((?:\s.*)?)(\])\s*$/;
const BLANKS = /^(\s*)(_{3,})\s*$/;
const OPTION = /^(\s*)([-*+]\s+)(\(\s?\)|\([xX]\)|\[\s?\]|\[[xX]\])?(\s*)(.*)$/;
const GOTO_SUFFIX = /(\s+)(->)(\s*)(\S+)\s*$/;
const OTHER = /^(.*?:?\s*)(_{3,})$/;
const HELP = /^(\s*)(>)(.*)$/;
const TABLE_ROW = /^\s*\|/;
const FENCE = /^\s*(```|~~~)/;
const HEADING = /^(#{1,6})(\s+.*)$/;
const ATTR_BLOCK = /(\s*)(\{[^{}]*\})\s*$/;
const FRONT_KEY = /^([A-Za-z][\w-]*)(\s*:\s*)(.*)$/;

type Block = 'body' | 'front' | 'question' | 'fence';

function span(cls: string, text: string): string {
  if (!text) return '';
  return `<span class="hl-${cls}">${escapeHtml(text)}</span>`;
}

/** Inline Markdown: emphasis, code, links. Kept deliberately small. */
function inline(text: string): string {
  let out = '';
  const re = /(`+)([^`]|[^`][\s\S]*?[^`])\1(?!`)|(\*\*|__)(?=\S)([\s\S]*?\S)\3|(\*|_)(?=\S)([^*_]*?\S)\5|\[([^\]]+)\]\(([^)\s]+)\)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    out += escapeHtml(text.slice(last, m.index));
    if (m[1] !== undefined) out += span('code', m[0]);
    else if (m[3] !== undefined) out += span('strong', m[0]);
    else if (m[5] !== undefined) out += span('em', m[0]);
    else out += `${span('punct', '[')}${span('link', m[7]!)}${span('punct', '](')}${span('url', m[8]!)}${span('punct', ')')}`;
    last = m.index + m[0].length;
  }
  return out + escapeHtml(text.slice(last));
}

function attrs(text: string): string {
  // key=value, key="quoted", flags, positional 1..5 and "labels"
  const re = /\s*(?:([A-Za-z][\w-]*)(=)("(?:[^"\\]|\\.)*"|\S*)|("(?:[^"\\]|\\.)*")|(\S+))/gy;
  let out = '';
  let pos = 0;
  let m: RegExpExecArray | null;
  while (pos < text.length && (m = re.exec(text))) {
    const ws = m[0].slice(0, m[0].length - m[0].trimStart().length);
    out += escapeHtml(ws);
    if (m[1] !== undefined) {
      out += span('attr', m[1]) + span('punct', '=');
      out += m[3]!.startsWith('"') ? span('str', m[3]!) : span('val', m[3]!);
    } else if (m[4] !== undefined) {
      out += span('str', m[4]);
    } else if (m[5] !== undefined) {
      out += /^-?\d+\.\.-?\d+$/.test(m[5]) || /^\d+$/.test(m[5]) ? span('num', m[5]) : span('flag', m[5]);
    }
    pos = re.lastIndex;
  }
  return out + escapeHtml(text.slice(pos));
}

function questionLine(text: string): string {
  let rest = text;
  let tail = '';
  const attr = ATTR_BLOCK.exec(rest);
  if (attr) {
    tail = escapeHtml(attr[1]!) + span('id', attr[2]!);
    rest = rest.slice(0, attr.index);
  }
  const trimmed = rest.replace(/\s+$/, '');
  const trailing = rest.slice(trimmed.length);
  if (trimmed.endsWith('*')) {
    const label = trimmed.slice(0, -1);
    return span('label', label) + span('req', '*') + escapeHtml(trailing) + tail;
  }
  return span('label', trimmed) + escapeHtml(trailing) + tail;
}

/** A choice's trailing `{value .flag}` block: braces punctuation, a bare word
 * is the value, `.word`s are flags. */
function choiceAttrs(block: string): string {
  const inner = block.slice(1, -1);
  let out = span('punct', '{');
  const re = /(\s+)|(\.[^\s.}]+)|(\S+)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(inner))) {
    if (m[1] !== undefined) out += escapeHtml(m[1]);
    else if (m[2] !== undefined) out += span('flag', m[2]);
    else out += span('value', m[3]!);
  }
  return out + span('punct', '}');
}

/** Splits a trailing choice attribute block off `text`. */
function splitChoiceAttrs(text: string): { rest: string; tail: string } {
  const a = ATTR_BLOCK.exec(text);
  if (!a) return { rest: text, tail: '' };
  const after = text.slice(a.index + a[1]!.length + a[2]!.length);
  return { rest: text.slice(0, a.index), tail: escapeHtml(a[1]!) + choiceAttrs(a[2]!) + escapeHtml(after) };
}

function optionLabel(text: string): string {
  let rest = text;
  let tail = '';
  const g = GOTO_SUFFIX.exec(rest);
  if (g) {
    tail = escapeHtml(g[1]!) + span('arrow', g[2]!) + escapeHtml(g[3]!) + span('target', g[4]!);
    rest = rest.slice(0, g.index);
  }
  const split = splitChoiceAttrs(rest);
  rest = split.rest;
  tail = split.tail + tail;
  const other = OTHER.exec(rest);
  if (other) return span('opt', other[1]!) + span('blank', other[2]!) + tail;
  return span('opt', rest) + tail;
}

function tableLine(text: string): string {
  return text
    .split('|')
    .map((cell) => {
      if (/^\s*:?-+:?\s*$/.test(cell)) return span('punct', cell);
      const { rest, tail } = splitChoiceAttrs(cell);
      return span('cell', rest) + tail;
    })
    .join(span('pipe', '|'));
}

function frontLine(line: string): string {
  if (line.trim() === '') return '';
  if (line.trimStart().startsWith('#')) return span('comment', line);
  const m = FRONT_KEY.exec(line);
  if (!m) return span('err-text', line);
  const [, key, sep, value] = m;
  let v: string;
  if (value === '|' || value === '>') v = span('punct', value!);
  else if (value === 'true' || value === 'false') v = span('bool', value);
  else if (/^["']/.test(value!)) v = span('str', value!);
  else {
    const c = /\s+#.*$/.exec(value!);
    v = c ? span('val', value!.slice(0, c.index)) + span('comment', c[0]) : span('val', value!);
  }
  return span('key', key!) + span('punct', sep!) + v;
}

function contentLine(line: string): string {
  const h = HEADING.exec(line);
  if (h) return span('heading', line);
  if (/^\s*([-*+]|\d+\.)\s+/.test(line)) {
    const m = /^(\s*)([-*+]|\d+\.)(\s+)(.*)$/.exec(line)!;
    return escapeHtml(m[1]!) + span('bullet', m[2]!) + escapeHtml(m[3]!) + inline(m[4]!);
  }
  if (/^\s*>/.test(line)) return span('quote', line);
  if (/^\s*\*\*\*\s*$/.test(line)) return span('punct', line);
  if (/^\\(\?|->|#)/.test(line)) return span('escape', line.slice(0, 1)) + inline(line.slice(1));
  return inline(line);
}

/** Wraps the given column range of a highlighted line in a diagnostic span.
 * Works on the escaped HTML by walking the text characters. */
function markLine(html: string, text: string, marks: LineMark[]): string {
  if (!marks.length) return html;
  // Build a per-character severity map (errors win over warnings).
  const len = Math.max(text.length, 1);
  const sev: (undefined | 'error' | 'warning')[] = new Array(len);
  for (const m of marks) {
    const start = Math.max(0, m.column - 1);
    const end = m.endColumn !== undefined ? Math.min(len, m.endColumn - 1) : len;
    for (let i = start; i < Math.max(end, start + 1); i++) {
      if (sev[i] !== 'error') sev[i] = m.severity;
    }
  }
  // Walk the HTML, tracking source character index, and open/close mark spans.
  let out = '';
  let idx = 0;
  let open: undefined | 'error' | 'warning';
  let i = 0;
  const openMark = (s: 'error' | 'warning') => {
    out += `<span class="hl-mark hl-mark-${s}">`;
    open = s;
  };
  const closeMark = () => {
    if (open) out += '</span>';
    open = undefined;
  };
  if (text.length === 0) {
    return `<span class="hl-mark hl-mark-${sev[0] ?? 'error'}"> </span>` + html;
  }
  while (i < html.length) {
    const ch = html[i]!;
    if (ch === '<') {
      const end = html.indexOf('>', i);
      const tag = html.slice(i, end + 1);
      // Close a mark before a tag boundary so nesting stays valid; the next
      // character reopens it if needed.
      closeMark();
      out += tag;
      i = end + 1;
      continue;
    }
    let consumed: string;
    if (ch === '&') {
      const end = html.indexOf(';', i);
      consumed = html.slice(i, end + 1);
      i = end + 1;
    } else {
      consumed = ch;
      i++;
    }
    const want = sev[idx];
    if (want !== open) {
      closeMark();
      if (want) openMark(want);
    }
    out += consumed;
    idx++;
  }
  closeMark();
  return out;
}

/**
 * Highlights every line of `source`, returning one HTML string per line
 * (without newlines). `marks` maps 1-based line numbers to diagnostic ranges
 * to underline.
 */
export function highlightLines(source: string, marks?: Map<number, LineMark[]>): string[] {
  const lines = source.replace(/\r\n?/g, '\n').split('\n');
  const out: string[] = [];
  let block: Block = 'body';
  let fenceMarker = '';

  if (lines[0]?.trim() === '---') {
    const end = lines.findIndex((l, idx) => idx > 0 && l.trim() === '---');
    if (end !== -1) block = 'front';
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;
    let html: string;

    if (block === 'front') {
      if (i === 0) html = span('fm', line);
      else if (line.trim() === '---') {
        html = span('fm', line);
        block = 'body';
      } else html = frontLine(line);
    } else if (block === 'fence') {
      html = span('code', line);
      if (FENCE.test(line) && line.trim().startsWith(fenceMarker)) block = 'body';
    } else {
      let m: RegExpExecArray | null;
      if ((m = PAGE_BREAK.exec(line))) {
        block = 'body';
        html =
          span('break', m[1]!) +
          escapeHtml(m[2]!) +
          (m[3] ? span('id', m[3]) + escapeHtml(m[4] ?? '') : '') +
          (m[5] ? span('arrow', m[5]) + escapeHtml(m[6] ?? '') + span('target', m[7]!) : '');
      } else if ((m = QUESTION.exec(line))) {
        block = 'question';
        html = span('q', m[1]!) + questionLine(m[2]!);
      } else if (block === 'question' && line.trim() !== '') {
        if ((m = HELP.exec(line))) {
          html = escapeHtml(m[1]!) + span('help-marker', m[2]!) + span('help', m[3]!);
        } else if ((m = TYPE_SPEC.exec(line))) {
          html =
            escapeHtml(m[1]!) + span('bracket', m[2]!) + span('type', m[3]!) + attrs(m[4]!) + span('bracket', m[5]!);
        } else if ((m = BLANKS.exec(line))) {
          html = escapeHtml(m[1]!) + span('blank', m[2]!);
        } else if ((m = OPTION.exec(line))) {
          html =
            escapeHtml(m[1]!) +
            span('bullet', m[2]!) +
            (m[3] ? span(/[xX]/.test(m[3]) ? 'marker-on' : 'marker', m[3]) : '') +
            escapeHtml(m[4]!) +
            optionLabel(m[5]!);
        } else if (TABLE_ROW.test(line)) {
          html = tableLine(line);
        } else {
          html = span('err-text', line);
        }
      } else if ((m = PAGE_JUMP.exec(line))) {
        block = 'body';
        html = span('arrow', m[1]!) + escapeHtml(m[2]!) + span('target', m[3]!);
      } else if (FENCE.test(line)) {
        block = 'fence';
        fenceMarker = FENCE.exec(line)![1]!;
        html = span('code', line);
      } else {
        if (line.trim() === '') block = 'body';
        html = contentLine(line);
      }
    }

    const lineMarks = marks?.get(i + 1);
    out.push(lineMarks ? markLine(html, line, lineMarks) : html);
  }
  return out;
}

/** Groups diagnostics by line for highlightLines(). */
export function marksFromDiagnostics(diagnostics: Diagnostic[]): Map<number, LineMark[]> {
  const map = new Map<number, LineMark[]>();
  for (const d of diagnostics) {
    const list = map.get(d.line) ?? [];
    list.push({ severity: d.severity, column: d.column, endColumn: d.endColumn });
    map.set(d.line, list);
  }
  return map;
}

/** Highlights a whole document as one HTML string (lines joined with "\n"). */
export function highlightAskdown(source: string, diagnostics?: Diagnostic[]): string {
  return highlightLines(source, diagnostics ? marksFromDiagnostics(diagnostics) : undefined).join('\n');
}
